import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trash2, ShieldAlert, Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '../utils/api';
import { COUNTRIES } from '../utils/constants';
import { normalizePhoneNumber } from '../utils/phoneUtils';
import Seo from '../components/Seo';

const DeleteAccount = () => {
  const navigate = useNavigate();
  const [country, setCountry] = useState('USA');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [step, setStep] = useState('phone'); // phone | otp | done
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const normalized = () => {
    const c = COUNTRIES.find((x) => x.code === country);
    return normalizePhoneNumber(phone, c?.dialCode);
  };

  const handleSendOtp = async () => {
    if (!phone.trim()) return toast.error('Enter your WhatsApp number');
    setSending(true);
    try {
      await authAPI.sendOTP(normalized());
      toast.success('Verification code sent via WhatsApp');
      setStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send code. Make sure this number has WhatsApp.');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') return toast.error('Please type DELETE to confirm');
    if (!otp.trim()) return toast.error('Enter the verification code');
    setDeleting(true);
    try {
      await authAPI.webDeleteAccount(normalized(), otp.trim());
      setStep('done');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-gray-900 flex flex-col">
      <Seo title="Delete your MoneyFeed account" description="Request deletion of your MoneyFeed account and associated data." path="/delete-account" noindex />
      <header className="bg-[#064E3B] text-white py-4 shadow-lg">
        <div className="container mx-auto px-4 flex items-center gap-3">
          <button onClick={() => navigate('/')} className="text-white/80 hover:text-white" data-testid="delete-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-xl md:text-2xl font-bold">Delete Your MoneyFeed Account</h1>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-4 py-8 max-w-lg">
        <Card className="p-6 bg-white dark:bg-gray-800 shadow-md">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-lg bg-red-100 dark:bg-red-900 flex items-center justify-center">
              <ShieldAlert className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800 dark:text-white">Request account &amp; data deletion</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">MoneyFeed (com.moneyfeed.app)</p>
            </div>
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-5 text-sm text-amber-900 dark:text-amber-300" data-testid="delete-export-first-reminder">
            <p className="font-semibold mb-1">Want a copy of your data first?</p>
            <p>
              Deletion is permanent and can't be undone. Before you continue, open the MoneyFeed app and go to
              <span className="font-medium"> Settings → Export Data</span> to download your transactions, bills and receipts (CSV or full ZIP archive).
            </p>
          </div>

          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-5 text-sm text-red-800 dark:text-red-300">
            <p className="font-semibold mb-1">This permanently deletes:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Your account &amp; profile (phone number, name)</li>
              <li>All transactions and financial records</li>
              <li>All bills and reminders</li>
              <li>Connected bank links and account balances</li>
              <li>Team/family invitations</li>
            </ul>
            <p className="mt-2">This action cannot be undone. No data is retained.</p>
          </div>

          <div className="bg-slate-50 dark:bg-gray-700/40 border border-slate-200 dark:border-gray-700 rounded-lg p-4 mb-5 text-sm text-gray-700 dark:text-gray-300">
            <p className="font-semibold mb-1 text-gray-800 dark:text-white">Prefer to delete only some data (keep your account)?</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Delete individual transactions in the app: <span className="font-medium">Transactions → 🗑️ on any item</span></li>
              <li>Delete individual bills: <span className="font-medium">Bills → 🗑️ on any item</span></li>
              <li>Disconnect a linked bank: <span className="font-medium">Settings → Connected Banks → Remove</span></li>
            </ul>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              <span className="font-semibold">Data retention:</span> Deleted data is removed from our active database immediately.
              Encrypted backups are purged within 30 days. We keep no personal data after that period.
            </p>
          </div>

          {step === 'done' ? (
            <div className="text-center py-6" data-testid="delete-success">
              <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-gray-800 dark:text-white">Account deleted</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Your account and all associated data have been permanently deleted.
              </p>
              <Button onClick={() => navigate('/')} className="mt-4 bg-[#064E3B] hover:bg-[#053e2f] text-white">
                Return home
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>Country</Label>
                <Select value={country} onValueChange={setCountry} disabled={step === 'otp'}>
                  <SelectTrigger className="mt-2" data-testid="delete-country-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.dialCode} - {c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Your WhatsApp number (the one on your account)</Label>
                <Input
                  type="tel"
                  placeholder="1234567890"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-2"
                  disabled={step === 'otp'}
                  data-testid="delete-phone-input"
                />
              </div>

              {step === 'phone' && (
                <Button onClick={handleSendOtp} disabled={sending} className="w-full bg-[#064E3B] hover:bg-[#053e2f] text-white" data-testid="delete-send-otp-btn">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send verification code'}
                </Button>
              )}

              {step === 'otp' && (
                <>
                  <div>
                    <Label>Verification code (sent to your WhatsApp)</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="6-digit code"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      className="mt-2"
                      data-testid="delete-otp-input"
                    />
                  </div>
                  <div>
                    <Label>Type "DELETE" to confirm</Label>
                    <Input
                      type="text"
                      placeholder="DELETE"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      className="mt-2"
                      data-testid="delete-confirm-input"
                    />
                  </div>
                  <Button onClick={handleDelete} disabled={deleting} className="w-full bg-red-600 hover:bg-red-700 text-white" data-testid="delete-confirm-btn">
                    {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4 mr-2" />Permanently delete my account</>}
                  </Button>
                  <button onClick={() => setStep('phone')} className="text-xs text-gray-500 hover:underline w-full text-center">
                    Use a different number
                  </button>
                </>
              )}
            </div>
          )}

          <p className="text-xs text-gray-400 dark:text-gray-500 mt-6 text-center">
            Trouble deleting? Email <a href="mailto:support@moneyfeed.io" className="underline">support@moneyfeed.io</a> and we'll remove your data within 30 days.
          </p>
        </Card>
      </main>
    </div>
  );
};

export default DeleteAccount;
